import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  BellRing,
  FileDown,
  FileSignature,
  Info,
  ListTree,
  PenLine,
  Percent,
  Plus,
  Ruler,
  Send,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useDeferredValue, useMemo, useRef, useState } from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { ShareDialog, shareResultSchema, type ShareResult } from '@/components/share-dialog';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError, openAuthedFile, request } from '@/lib/api';
import { customerSchema } from '@/lib/schemas';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { fetchPriceList, PRICE_LIST_QUERY_KEY } from '@/features/price-list/price-list-api';
import { PROCESSES_KEY, processesApi } from '@/features/processes/processes-api';
import { BUSINESS_SETTINGS_KEY, businessApi, type PaymentTerms } from '@/features/settings/business-api';
import { InlineNewCustomer } from '@/features/customers/inline-new-customer';
import { useTrade } from '@/lib/trade';
import { DrawingQuoteDialog } from './drawing-quote-dialog';
import { balanceSchedule, scheduleIssue } from './payment-terms';
import { PaymentTermsEditor, useScheduleIssueText } from './payment-terms-editor';
import {
  discountAgorot,
  discountBody,
  discountIssue,
  quoteTotals,
  type DiscountDraft,
  type DiscountIssue,
} from './quote-discount';
import { ALLOCATION_THRESHOLD, needsTaxIdForAllocation } from './allocation';
import { quoteGross, quoteSchema, type Quote } from './quotes-api';
import { useCanEdit } from '@/lib/auth';

/* ---------------------------------------------------------------------------
   הצעות מחיר.

   הצעה נבנית מהמחירון ומשורות חופשיות (שאפשר לשמור גם במחירון). המחיר
   שהלקוח מאשר הוא בדיוק מה שהחשבונית תחייב בסוף — גם אם המחירון ישתנה.
   הלקוח מאשר מקישור, והאישור הופך למשימה חדשה.
   --------------------------------------------------------------------------- */

export type { Quote };

const STATUS_TONE: Record<Quote['status'], 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
  DRAFT: 'neutral',
  SENT: 'info',
  APPROVED: 'success',
  DECLINED: 'danger',
  EXPIRED: 'warning',
};

const QUOTES_KEY = 'quotes';

export function QuotesPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['quotes', 'common']);
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [fromDrawing, setFromDrawing] = useState(false);
  const [shared, setShared] = useState<ShareResult | null>(null);

  // "הצעה משרטוט" — רק לנגרות, ורק כשהתהליך מופעל בעסק (מרקטפלייס התהליכים).
  // השרת מסמן relevant=false לתחום שהתהליך לא משרת, גם אם הופעל בעבר.
  const trade = useTrade();
  const isCarpentry = trade.key === 'CARPENTRY';
  const processes = useQuery({
    queryKey: PROCESSES_KEY,
    queryFn: ({ signal }) => processesApi.list(signal),
    enabled: isCarpentry,
  });
  const drawingEnabled =
    isCarpentry && (processes.data?.some((p) => p.key === 'carpentry-drawing-quote' && p.enabled && p.relevant) ?? false);

  const list = useQuery({
    queryKey: [QUOTES_KEY],
    queryFn: ({ signal }) => request('/quotes', { schema: z.array(quoteSchema), signal }),
  });

  const send = useMutation({
    mutationFn: (id: string) => request(`/quotes/${id}/send`, { method: 'POST', schema: shareResultSchema }),
    onSuccess: (r) => {
      if (r.autoSent) toast.success(t('toast.sentWhatsApp'), t('toast.sentWhatsAppDetail'));
      else setShared(r);
      void qc.invalidateQueries({ queryKey: [QUOTES_KEY] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast.error(t('toast.sendFailed'), e instanceof ApiError ? e.message : undefined),
  });

  // תהליך "תזכורות להצעת מחיר" (ADR 0003): השהיה/המשך להצעה אחת.
  const reminders = useMutation({
    mutationFn: ({ id, pause }: { id: string; pause: boolean }) =>
      request(`/quotes/${id}/reminders/${pause ? 'pause' : 'resume'}`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [QUOTES_KEY] }),
    onError: (e) => toast.error(t('toast.updateFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const now = Date.now();
  const rows = list.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              {drawingEnabled ? (
                <Button size="sm" variant="secondary" onClick={() => setFromDrawing(true)}>
                  <Ruler aria-hidden />
                  {t('page.fromDrawing')}
                </Button>
              ) : null}
              <Button size="sm" onClick={() => setCreating(true)} data-track="quote.new">
                <Plus aria-hidden />
                {t('page.new')}
              </Button>
            </div>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        {list.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title={t('page.emptyTitle')}
            description={t('page.emptyDescription')}
            action={
              canEdit ? (
                <Button size="sm" onClick={() => setCreating(true)} data-track="quote.new">
                  <Plus aria-hidden />
                  {t('page.new')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((q) => {
              const expired = q.status === 'SENT' && new Date(q.validUntil).getTime() < now;
              const status = expired ? 'EXPIRED' : q.status;
              const canSend = canEdit && (q.status === 'DRAFT' || q.status === 'SENT') && !expired;
              return (
                <li key={q.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
                  <span className="tabular w-14 text-sm text-fg-muted">#{q.quoteNumber}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-fg">{q.customer.name}</p>
                    <p className="truncate text-xs text-fg-muted">
                      {q.lines.map((l) => (l.qty > 1 ? `${l.description} ×${l.qty}` : l.description)).join(' · ')}
                    </p>
                    {q.signedAt ? (
                      <span className="mt-0.5 flex items-center gap-1 text-2xs text-success">
                        <PenLine className="size-3" aria-hidden />
                        {t('list.signedBy', { name: q.signerName, date: formatDate(q.signedAt) })}
                      </span>
                    ) : null}
                    {q.workPlan && q.taskId ? (
                      <Link
                        to="/tasks/$taskId"
                        params={{ taskId: q.taskId }}
                        className="mt-0.5 flex items-center gap-1 text-2xs text-accent hover:underline"
                      >
                        <ListTree className="size-3" aria-hidden />
                        {t('list.tasksOpened', { count: q.workPlan.tasks })}
                        {q.workPlan.admin ? t('list.forAdmin', { count: q.workPlan.admin }) : ''}
                        {q.workPlan.firstVisit
                          ? t('list.firstVisit', { name: q.workPlan.firstVisit.userName, date: formatDateTime(q.workPlan.firstVisit.start) })
                          : q.workPlan.visits
                            ? t('list.notScheduled')
                            : ''}
                      </Link>
                    ) : null}
                    {q.status === 'SENT' && !expired && (q.nextReminderAt || q.remindersPaused) ? (
                      <span className="mt-0.5 flex items-center gap-2 text-2xs text-fg-subtle">
                        <BellRing className="size-3" aria-hidden />
                        {q.remindersPaused
                          ? t('list.remindersPaused')
                          : t('list.nextReminder', { n: q.remindersSent + 1, date: formatDate(q.nextReminderAt) })}
                        {canEdit ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-2xs"
                            disabled={reminders.isPending}
                            onClick={() => reminders.mutate({ id: q.id, pause: !q.remindersPaused })}
                          >
                            {q.remindersPaused ? t('list.resume') : t('list.pause')}
                          </Button>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                  <span className="flex flex-col items-end">
                    <span className="tabular text-sm font-medium text-fg">{formatCurrency(quoteGross(q))}</span>
                    {Number(q.discountAmount ?? 0) > 0 ? (
                      <span className="tabular text-2xs text-success">
                        {t('list.discount', { amount: formatCurrency(q.discountAmount ?? 0) })}
                      </span>
                    ) : null}
                  </span>
                  <Badge tone={STATUS_TONE[status]}>{t(`status.${status}`)}</Badge>
                  <span className="hidden text-xs text-fg-subtle sm:inline">{t('list.validUntil', { date: formatDate(q.validUntil) })}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('list.pdfAria', { number: q.quoteNumber })}
                    onClick={() =>
                      void openAuthedFile(`/quotes/${q.id}/pdf`).catch(() => toast.error(t('list.pdfFailed')))
                    }
                  >
                    <FileDown aria-hidden />
                    PDF
                  </Button>
                  {canSend ? (
                    <Button
                      size="sm"
                      variant={q.status === 'DRAFT' ? 'primary' : 'secondary'}
                      loading={send.isPending && send.variables === q.id}
                      onClick={() => send.mutate(q.id)}
                      data-track="quote.send"
                    >
                      <Send aria-hidden />
                      {q.status === 'DRAFT' ? t('list.send') : t('list.resend')}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {creating ? (
        <NewQuoteDialog
          onClose={() => setCreating(false)}
          onCreated={(q) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: [QUOTES_KEY] });
            void qc.invalidateQueries({ queryKey: ['dashboard'] });
            send.mutate(q.id);
          }}
        />
      ) : null}

      {fromDrawing ? (
        <DrawingQuoteDialog
          onClose={() => setFromDrawing(false)}
          onCreated={(q) => {
            setFromDrawing(false);
            void qc.invalidateQueries({ queryKey: [QUOTES_KEY] });
            send.mutate(q.id);
          }}
        />
      ) : null}

      {shared ? (
        <ShareDialog
          result={shared}
          title={t('share.title')}
          description={t('share.description')}
          onClose={() => setShared(null)}
        />
      ) : null}
    </div>
  );
}

type PickedCustomer = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  taxId?: string | null;
};

/** ללקוח יש לאן לשלוח: טלפון (WhatsApp) או מייל (העותק החתום). */
const hasContact = (c: PickedCustomer) => !!c.phone?.trim() || !!c.email?.trim();

/** שורה חופשית — לא מהמחירון. המחיר כמחרוזת עשרונית, כמו במחירון ובשרת. */
export type FreeLine = {
  id: number;
  description: string;
  qty: number;
  unitPrice: string;
  save: boolean;
};
type FreeLineIssue = 'descriptionRequired' | 'descriptionTooLong' | 'priceInvalid' | 'qtyInvalid';

/** כמו PRICE_PATTERN בשרת: לא שלילי, עד 2 ספרות אחרי הנקודה. */
const PRICE_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
const MAX_FREE_DESCRIPTION = 300;

export function freeLineIssues(
  l: Omit<FreeLine, 'id'>,
): Partial<Record<'description' | 'unitPrice' | 'qty', FreeLineIssue>> {
  const issues: Partial<Record<'description' | 'unitPrice' | 'qty', FreeLineIssue>> = {};
  const description = l.description.trim();
  if (!description) issues.description = 'descriptionRequired';
  else if (description.length > MAX_FREE_DESCRIPTION) issues.description = 'descriptionTooLong';
  if (!PRICE_PATTERN.test(l.unitPrice.trim())) issues.unitPrice = 'priceInvalid';
  if (!Number.isInteger(l.qty) || l.qty < 1 || l.qty > 10_000) issues.qty = 'qtyInvalid';
  return issues;
}
const isValidFreeLine = (l: Omit<FreeLine, 'id'>) => Object.keys(freeLineIssues(l)).length === 0;
const EMPTY_DRAFT = { description: '', qty: 1, unitPrice: '', save: true };
const NO_DISCOUNT: DiscountDraft = { type: 'PERCENT', value: '', label: '' };

export function NewQuoteDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (q: Quote) => void }) {
  const { t } = useTranslation(['quotes', 'common']);
  const toast = useToast();
  const scheduleIssueText = useScheduleIssueText();
  const [search, setSearch] = useState('');
  const term = useDeferredValue(search.trim());
  const [customer, setCustomer] = useState<PickedCustomer | null>(null);
  const [addingCustomer, setAddingCustomer] = useState(false);
  // קוד → כמות. סדר ההכנסה הוא סדר השורות בהצעה.
  const [lines, setLines] = useState<Array<{ code: string; qty: number }>>([]);
  // שורות חופשיות שנוספו, והשורה שבעריכה. "לשמור גם במחירון" מסומן כברירת מחדל.
  const [freeLines, setFreeLines] = useState<FreeLine[]>([]);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [draftTouched, setDraftTouched] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);
  const nextFreeId = useRef(1);
  const [notes, setNotes] = useState('');
  // הנחה על כל ההצעה — שורה נפרדת, לפני מע"מ. ריק = בלי הנחה.
  const [discount, setDiscount] = useState<DiscountDraft>(NO_DISCOUNT);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [terms, setTerms] = useState<PaymentTerms | null>(null);
  const [editTerms, setEditTerms] = useState(false);
  // שגיאת השרת האחרונה — נשארת מוצגת ליד הכפתור (ה-toast נעלם).
  const [serverError, setServerError] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ['customer-search', term],
    queryFn: ({ signal }) =>
      request(`/customers/search?q=${encodeURIComponent(term)}`, { schema: z.array(customerSchema), signal }),
    enabled: term.length >= 2 && !customer,
  });
  const prices = useQuery({
    queryKey: [PRICE_LIST_QUERY_KEY, false],
    queryFn: ({ signal }) => fetchPriceList(false, signal),
  });
  // ברירות המחדל של העסק (מע"מ ולוח תשלומים). בלעדיהן — ההצעה נוצרת עם מה שבשרת.
  const business = useQuery({
    queryKey: BUSINESS_SETTINGS_KEY,
    queryFn: ({ signal }) => businessApi.get(signal),
    retry: false,
  });
  const defaults = business.data?.quoteDefaults;
  const shownTerms = terms ?? defaults?.paymentTerms ?? null;

  const totals = useMemo(() => {
    // לתצוגה בלבד. השרת מחשב ב-Decimal מהמחירון שלו.
    const byCode = new Map((prices.data ?? []).map((p) => [p.code, p]));
    const catalog = lines.reduce(
      (sum, l) => sum + Math.round(Number(byCode.get(l.code)?.price ?? 0) * 100) * l.qty,
      0,
    );
    const free = freeLines.reduce(
      (sum, l) =>
        sum +
        (PRICE_PATTERN.test(l.unitPrice.trim())
          ? Math.round(Number(l.unitPrice) * 100) * l.qty
          : 0),
      0,
    );
    const agorot = catalog + free;
    const mode = defaults?.vatMode ?? 'exempt';
    const rate = defaults?.vatRate ?? 0;
    // שורות → הנחה → מע"מ על מה שאחרי ההנחה (quote-discount.ts, כמו בשרת).
    const off = discountAgorot(discount, agorot);
    const sums = quoteTotals(agorot, off, mode, rate);
    return {
      subtotal: sums.subtotal / 100,
      discount: sums.discount / 100,
      net: sums.afterDiscount / 100,
      vat: sums.vat / 100,
      gross: sums.gross / 100,
      subtotalAgorot: agorot,
      netAgorot: sums.net,
      mode,
      rate,
    };
  }, [prices.data, lines, freeLines, defaults, discount]);
  const discountProblem = discountIssue(discount, totals.subtotalAgorot);

  // מפתח אידמפוטנטיות אחד לכל *הגשה*: ניסיון חוזר של אותו תוכן (לחיצה
  // כפולה, "נסה שוב" אחרי timeout) שולח את אותו מפתח, והשרת מחזיר את
  // ההצעה שכבר נוצרה במקום ליצור שנייה עם מספר רץ חדש. תוכן שונה מקבל
  // מפתח חדש — אחרת השרת היה דוחה ב-422 (מפתח זהה, גוף אחר).
  const submission = useRef<{ payload: string; key: string } | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const body = {
        customerId: customer!.id,
        lines: [
          ...lines.map((l) => ({ priceCode: l.code, qty: l.qty })),
          ...freeLines.map((l) => ({
            description: l.description.trim(),
            unitPrice: l.unitPrice.trim(),
            qty: l.qty,
            saveToPriceList: l.save,
          })),
        ],
        ...(notes.trim() && { notes: notes.trim() }),
        ...(discountBody(discount) && { discount: discountBody(discount) }),
        // רק אם שונו בהצעה הזו — אחרת השרת לוקח את ברירת המחדל העדכנית.
        ...(terms && { paymentTerms: terms }),
      };
      const payload = JSON.stringify(body);
      if (submission.current?.payload !== payload) {
        submission.current = { payload, key: crypto.randomUUID() };
      }
      return request('/quotes', {
        method: 'POST',
        body,
        schema: quoteSchema,
        idempotencyKey: submission.current.key,
      });
    },
    onMutate: () => setServerError(null),
    onSuccess: onCreated,
    onError: (e) => {
      const message = e instanceof ApiError ? e.message : t('common:errors.generic');
      setServerError(message);
      toast.error(t('toast.createFailed'), message);
    },
  });

  const toggle = (code: string) =>
    setLines((ls) => (ls.some((l) => l.code === code) ? ls.filter((l) => l.code !== code) : [...ls, { code, qty: 1 }]));
  const setQty = (code: string, qty: number) =>
    setLines((ls) => ls.map((l) => (l.code === code ? { ...l, qty: Math.min(10_000, Math.max(1, Math.round(qty) || 1)) } : l)));

  const addDraft = () => {
    setDraftTouched(true);
    if (!isValidFreeLine(draft)) return;
    setFreeLines((ls) => [
      ...ls,
      {
        ...draft,
        description: draft.description.trim(),
        unitPrice: draft.unitPrice.trim(),
        id: nextFreeId.current++,
      },
    ]);
    setDraft(EMPTY_DRAFT);
    setDraftTouched(false);
  };
  const updateFree = (id: number, patch: Partial<FreeLine>) =>
    setFreeLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeFree = (id: number) => setFreeLines((ls) => ls.filter((l) => l.id !== id));

  // מחירון ריק אינו שגיאה: בונים את ההצעה משורות חופשיות, והטופס פתוח מראש.
  const priceListEmpty = prices.data?.length === 0;
  const showFreeForm = freeOpen || priceListEmpty || freeLines.length > 0;
  const draftStarted = draft.description.trim() !== '' || draft.unitPrice.trim() !== '';

  // כל מה שחוסם יצירה ושליחה — מוצג גם ליד השדה וגם מסוכם ליד הכפתור.
  const termsIssue = shownTerms ? scheduleIssue(shownTerms.schedule) : null;
  const blockers: string[] = [];
  if (!customer) blockers.push(addingCustomer ? t('new.blocked.customerFormOpen') : t('new.blocked.noCustomer'));
  else if (!hasContact(customer)) blockers.push(t('new.blocked.noContact', { name: customer.name }));
  if (lines.length === 0 && freeLines.length === 0) blockers.push(t('new.blocked.noLines'));
  if (freeLines.some((l) => !isValidFreeLine(l))) blockers.push(t('new.blocked.freeLineInvalid'));
  if (draftStarted) blockers.push(t('new.blocked.freeLineNotAdded'));
  if (termsIssue) blockers.push(scheduleIssueText(termsIssue));
  if (discountProblem) blockers.push(t('new.blocked.discountInvalid'));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl" aria-describedby="new-quote-desc">
        <DialogHeader>
          <DialogTitle>{t('new.title')}</DialogTitle>
          <DialogDescription id="new-quote-desc">
            {t('new.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {customer ? (
            <div className="space-y-2 rounded-lg border border-border px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-fg">{customer.name}</span>
                <Button size="sm" variant="ghost" onClick={() => setCustomer(null)}>
                  {t('form.change')}
                </Button>
              </div>
              {hasContact(customer) ? null : (
                <CustomerContactFix customer={customer} onSaved={(c) => setCustomer({ ...customer, ...c })} />
              )}
            </div>
          ) : addingCustomer ? (
            <InlineNewCustomer
              initialName={search.trim()}
              onCancel={() => setAddingCustomer(false)}
              onCreated={(c) => {
                setCustomer(c);
                setAddingCustomer(false);
              }}
            />
          ) : (
            // Field מקבל ילד אחד בדיוק (הוא משכפל אותו כדי לשים עליו את
            // ה-aria). רשימת התוצאות יושבת לכן מחוץ לו, והעוטף שומר אותה
            // צמודה לשדה ולא במרווח המלא של גוף הדיאלוג.
            <div>
              <Field label={t('form.customer')} htmlFor="q-customer" required hint={t('form.customerHint')}>
                <Input id="q-customer" autoFocus value={search} onChange={(e) => setSearch(e.target.value)} />
              </Field>
              {customers.data && customers.data.length > 0 ? (
                <ul className="mt-1 max-h-40 overflow-auto rounded-lg border border-border">
                  {customers.data.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                        onClick={() => setCustomer({ id: c.id, name: c.name, email: c.email, phone: c.phone, taxId: c.taxId })}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {term.length >= 2 && customers.data?.length === 0 ? (
                <p className="mt-1 text-xs text-fg-muted">{t('new.noCustomer')}</p>
              ) : null}
              <Button type="button" size="sm" variant="ghost" className="mt-1" onClick={() => setAddingCustomer(true)}>
                <UserPlus aria-hidden />
                {t('new.newCustomer')}
              </Button>
            </div>
          )}

          <fieldset
            className="space-y-1"
            aria-describedby={lines.length === 0 && freeLines.length === 0 ? 'q-lines-hint' : undefined}
          >
            <legend className="mb-2 text-sm font-medium text-fg">{t('new.linesLegend')}</legend>
            {prices.isLoading ? <Skeleton className="h-24 w-full" /> : null}
            {priceListEmpty ? (
              <p className="text-sm text-fg-muted">{t('new.emptyPriceList')}</p>
            ) : lines.length === 0 && freeLines.length === 0 && prices.data ? (
              <p id="q-lines-hint" className="text-xs text-fg-muted">{t('new.blocked.noLines')}</p>
            ) : null}
            <div className="max-h-64 space-y-1 overflow-auto">
              {prices.data?.map((p) => {
                const line = lines.find((l) => l.code === p.code);
                return (
                  <div key={p.id} className="flex min-h-11 items-center gap-3 rounded-lg px-2 hover:bg-surface-sunken">
                    <label className="flex flex-1 cursor-pointer items-center gap-3">
                      <Checkbox checked={!!line} onCheckedChange={() => toggle(p.code)} />
                      <span className="flex-1 text-sm text-fg">{p.description}</span>
                    </label>
                    {line ? (
                      <Input
                        type="number"
                        dir="ltr"
                        inputMode="numeric"
                        min={1}
                        max={10000}
                        className="h-9 w-20"
                        aria-label={t('new.qtyAria', { item: p.description })}
                        value={line.qty}
                        onChange={(e) => setQty(p.code, Number(e.target.value))}
                      />
                    ) : null}
                    <span className="w-24 text-end tabular text-sm text-fg-muted">
                      {line && line.qty > 1 ? formatCurrency(Number(p.price) * line.qty) : formatCurrency(p.price)}
                    </span>
                  </div>
                );
              })}
            </div>
          </fieldset>

          {showFreeForm ? (
            <FreeLinesSection
              lines={freeLines}
              draft={draft}
              draftTouched={draftTouched}
              onDraftChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
              onAdd={addDraft}
              onUpdate={updateFree}
              onRemove={removeFree}
            />
          ) : (
            <Button type="button" size="sm" variant="ghost" onClick={() => setFreeOpen(true)}>
              <Plus aria-hidden />
              {t('new.free.open')}
            </Button>
          )}

          {discountOpen || discount.value !== '' ? (
            <DiscountSection
              value={discount}
              issue={discountProblem}
              onChange={(patch) => setDiscount((d) => ({ ...d, ...patch }))}
              onRemove={() => {
                setDiscount(NO_DISCOUNT);
                setDiscountOpen(false);
              }}
            />
          ) : (
            <Button type="button" size="sm" variant="ghost" onClick={() => setDiscountOpen(true)}>
              <Percent aria-hidden />
              {t('new.discount.open')}
            </Button>
          )}

          <Field label={t('form.notes')} htmlFor="q-notes">
            <Textarea id="q-notes" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          {shownTerms ? (
            <section className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-fg">{t('new.paymentTerms')}</p>
                  <p className="text-xs text-fg-muted">
                    {shownTerms.schedule.map((r) => `${r.label} ${r.percent}%`).join(' · ')}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditTerms((v) => !v);
                    if (!terms) setTerms({ ...shownTerms, schedule: balanceSchedule(shownTerms.schedule) });
                  }}
                >
                  {editTerms ? t('common:actions.close') : t('new.editTerms')}
                </Button>
              </div>
              {editTerms && terms ? (
                <PaymentTermsEditor
                  value={terms}
                  onChange={setTerms}
                  totalLabel={(percent) => formatCurrency((totals.gross * percent) / 100)}
                />
              ) : termsIssue ? (
                <p className="text-xs font-medium text-danger">{scheduleIssueText(termsIssue)}</p>
              ) : null}
            </section>
          ) : null}

          <div className="space-y-1 border-t border-border pt-3 text-sm" data-testid="q-totals">
            {totals.discount > 0 ? (
              <>
                <p className="flex justify-between text-fg-muted">
                  <span>{t('new.discount.subtotal')}</span>
                  <span className="tabular">{formatCurrency(totals.subtotal)}</span>
                </p>
                <p className="flex justify-between text-success">
                  <span>
                    {discount.label.trim() || t('new.discount.row')}
                    {discount.type === 'PERCENT' ? ` (${discount.value.trim()}%)` : ''}
                  </span>
                  <span className="tabular">{formatCurrency(-totals.discount)}</span>
                </p>
              </>
            ) : null}
            {totals.mode === 'added' ? (
              <>
                <p className="flex justify-between text-fg-muted">
                  <span>{t('new.beforeVat')}</span>
                  <span className="tabular">{formatCurrency(totals.net)}</span>
                </p>
                <p className="flex justify-between text-fg-muted">
                  <span>{t('new.vat', { rate: totals.rate })}</span>
                  <span className="tabular">{formatCurrency(totals.vat)}</span>
                </p>
              </>
            ) : null}
            <p className="flex justify-between">
              <span className="text-fg-muted">{totals.mode === 'included' ? t('new.totalInclVat') : t('form.total')}</span>
              <span className="tabular font-semibold text-fg">{formatCurrency(totals.gross)}</span>
            </p>
          </div>

          {customer && needsTaxIdForAllocation(totals.netAgorot, totals.mode, customer.taxId) ? (
            <p className="flex items-start gap-2 rounded-lg bg-surface-sunken p-3 text-xs text-fg-muted" role="note">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                {t('new.allocationTaxId', {
                  name: customer.name,
                  threshold: formatCurrency(ALLOCATION_THRESHOLD),
                })}
              </span>
            </p>
          ) : null}

          {blockers.length > 0 ? (
            <div id="q-blockers" className="rounded-lg bg-surface-sunken p-3 text-sm" role="status">
              <p className="font-medium text-fg">{t('new.blocked.title')}</p>
              <ul className="mt-1 list-disc space-y-0.5 ps-5 text-fg-muted">
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {serverError ? (
            <p className="flex items-start gap-2 rounded-lg border border-danger/40 p-3 text-sm text-danger" role="alert">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                {t('toast.createFailed')}: {serverError}
              </span>
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={blockers.length > 0}
            aria-describedby={blockers.length > 0 ? 'q-blockers' : undefined}
            loading={create.isPending}
            onClick={() => create.mutate()}
            data-track="quote.create-send"
          >
            {t('form.createAndSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DISCOUNT_ERROR = {
  invalid: 'new.discount.errors.invalid',
  zero: 'new.discount.errors.zero',
  percentOver100: 'new.discount.errors.percentOver100',
  overSubtotal: 'new.discount.errors.overSubtotal',
} as const satisfies Record<DiscountIssue, ParseKeys<'quotes'>>;

/**
 * הנחה על כל ההצעה: אחוז או סכום, ותווית אופציונלית ("הנחת לקוח קבוע").
 * מוצגת כשורה נפרדת בהצעה, ב-PDF ובחשבונית — המחירים בשורות לא משתנים.
 */
function DiscountSection({
  value,
  issue,
  onChange,
  onRemove,
}: {
  value: DiscountDraft;
  issue: DiscountIssue | null;
  onChange: (patch: Partial<DiscountDraft>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('quotes');
  return (
    <fieldset className="space-y-2 rounded-lg border border-border p-3">
      <legend className="px-1 text-sm font-medium text-fg">{t('new.discount.legend')}</legend>
      <div className="flex flex-wrap items-end gap-2">
        <div role="group" aria-label={t('new.discount.typeAria')} className="flex rounded-lg border border-border p-0.5">
          {(['PERCENT', 'AMOUNT'] as const).map((type) => (
            <Button
              key={type}
              type="button"
              size="sm"
              variant={value.type === type ? 'primary' : 'ghost'}
              aria-pressed={value.type === type}
              onClick={() => onChange({ type })}
            >
              {type === 'PERCENT' ? t('new.discount.percent') : t('new.discount.amount')}
            </Button>
          ))}
        </div>
        <Field
          label={value.type === 'PERCENT' ? t('new.discount.valuePercent') : t('new.discount.valueAmount')}
          htmlFor="q-discount-value"
          error={issue ? t(DISCOUNT_ERROR[issue]) : undefined}
        >
          <Input
            id="q-discount-value"
            dir="ltr"
            inputMode="decimal"
            className="w-32"
            value={value.value}
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </Field>
        <Field label={t('new.discount.label')} htmlFor="q-discount-label">
          <Input
            id="q-discount-label"
            maxLength={80}
            placeholder={t('new.discount.labelPlaceholder')}
            value={value.label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </Field>
        <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
          {t('new.discount.remove')}
        </Button>
      </div>
      <p className="text-xs text-fg-muted">{t('new.discount.hint')}</p>
    </fieldset>
  );
}

/**
 * שורות חופשיות: טופס הוספה (תיאור, כמות, מחיר ליחידה, "לשמור גם במחירון")
 * ורשימה ניתנת לעריכה של מה שנוסף. הבדיקות כמו בשרת — ההודעות כאן, לא 400.
 */
function FreeLinesSection({
  lines,
  draft,
  draftTouched,
  onDraftChange,
  onAdd,
  onUpdate,
  onRemove,
}: {
  lines: FreeLine[];
  draft: Omit<FreeLine, 'id'>;
  draftTouched: boolean;
  onDraftChange: (patch: Partial<Omit<FreeLine, 'id'>>) => void;
  onAdd: () => void;
  onUpdate: (id: number, patch: Partial<FreeLine>) => void;
  onRemove: (id: number) => void;
}) {
  const { t } = useTranslation(['quotes', 'common']);
  const issue = (i?: FreeLineIssue) => (i ? t(`new.free.errors.${i}`) : undefined);
  const draftIssues = draftTouched ? freeLineIssues(draft) : {};
  const toQty = (v: string) => Math.min(10_000, Math.max(1, Math.round(Number(v)) || 1));

  return (
    <section
      className="space-y-3 rounded-lg border border-border p-3"
      aria-labelledby="q-free-title"
    >
      <h3 id="q-free-title" className="text-sm font-medium text-fg">
        {t('new.free.title')}
      </h3>

      {lines.length > 0 ? (
        <ul className="space-y-2" aria-label={t('new.free.addedLegend')}>
          {lines.map((l) => {
            const issues = freeLineIssues(l);
            return (
              <li key={l.id} className="space-y-1 rounded-lg bg-surface-sunken p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="h-9 min-w-40 flex-1"
                    maxLength={MAX_FREE_DESCRIPTION}
                    aria-label={t('new.free.description')}
                    aria-invalid={issues.description ? true : undefined}
                    value={l.description}
                    onChange={(e) => onUpdate(l.id, { description: e.target.value })}
                  />
                  <Input
                    type="number"
                    dir="ltr"
                    inputMode="numeric"
                    min={1}
                    max={10000}
                    className="h-9 w-20"
                    aria-label={t('new.qtyAria', { item: l.description })}
                    value={l.qty}
                    onChange={(e) => onUpdate(l.id, { qty: toQty(e.target.value) })}
                  />
                  <Input
                    dir="ltr"
                    inputMode="decimal"
                    className="h-9 w-28"
                    aria-label={t('new.free.unitPriceAria', { item: l.description })}
                    aria-invalid={issues.unitPrice ? true : undefined}
                    value={l.unitPrice}
                    onChange={(e) => onUpdate(l.id, { unitPrice: e.target.value })}
                  />
                  <span className="w-24 text-end tabular text-sm text-fg-muted">
                    {issues.unitPrice ? '—' : formatCurrency(Number(l.unitPrice) * l.qty)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('new.free.remove', { item: l.description })}
                    onClick={() => onRemove(l.id)}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-fg-muted">
                  <Checkbox
                    checked={l.save}
                    onCheckedChange={(v) => onUpdate(l.id, { save: v === true })}
                  />
                  {t('new.free.save')}
                </label>
                {Object.keys(issues).length > 0 ? (
                  <p className="text-xs font-medium text-danger">
                    {[issues.description, issues.qty, issues.unitPrice]
                      .filter(Boolean)
                      .map((i) => issue(i))
                      .join(' · ')}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="space-y-2">
        <Field
          label={t('new.free.description')}
          htmlFor="q-free-description"
          error={issue(draftIssues.description)}
        >
          <Input
            id="q-free-description"
            maxLength={MAX_FREE_DESCRIPTION}
            value={draft.description}
            onChange={(e) => onDraftChange({ description: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
          />
        </Field>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('new.free.qty')} htmlFor="q-free-qty" error={issue(draftIssues.qty)}>
            <Input
              id="q-free-qty"
              type="number"
              dir="ltr"
              inputMode="numeric"
              min={1}
              max={10000}
              value={draft.qty}
              onChange={(e) => onDraftChange({ qty: toQty(e.target.value) })}
            />
          </Field>
          <Field
            label={t('new.free.unitPrice')}
            htmlFor="q-free-price"
            hint={t('new.free.unitPriceHint')}
            error={issue(draftIssues.unitPrice)}
          >
            <Input
              id="q-free-price"
              dir="ltr"
              inputMode="decimal"
              placeholder="0.00"
              value={draft.unitPrice}
              onChange={(e) => onDraftChange({ unitPrice: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
            <Checkbox
              aria-describedby="q-free-save-hint"
              checked={draft.save}
              onCheckedChange={(v) => onDraftChange({ save: v === true })}
            />
            {t('new.free.save')}
          </label>
          <Button type="button" size="sm" variant="secondary" onClick={onAdd}>
            <Plus aria-hidden />
            {t('new.free.add')}
          </Button>
        </div>
        <p id="q-free-save-hint" className="text-xs text-fg-muted">
          {t('new.free.saveHint')}
        </p>
      </div>
    </section>
  );
}

/**
 * ללקוח שנבחר אין טלפון ואין מייל — אין לאן לשלוח את ההצעה ואת העותק החתום.
 * משלימים כאן (PATCH /customers/:id) בלי לצאת מהדיאלוג.
 */
function CustomerContactFix({
  customer,
  onSaved,
}: {
  customer: PickedCustomer;
  onSaved: (c: { phone: string | null; email: string | null }) => void;
}) {
  const { t } = useTranslation(['quotes', 'common']);
  const toast = useToast();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const emailInvalid = email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const empty = !phone.trim() && !email.trim();

  const save = useMutation({
    mutationFn: () =>
      request(`/customers/${customer.id}`, {
        method: 'PATCH',
        body: {
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(email.trim() ? { email: email.trim().toLowerCase() } : {}),
        },
        schema: customerSchema,
      }),
    onSuccess: (c) => {
      toast.success(t('new.contact.saved'));
      onSaved({ phone: c.phone ?? null, email: c.email ?? null });
    },
    onError: (e) => toast.error(t('new.contact.failed'), e instanceof ApiError ? e.message : undefined),
  });

  return (
    <div className="space-y-2 border-t border-border pt-2">
      <p className="text-xs font-medium text-danger" role="alert">
        {t('new.blocked.noContact', { name: customer.name })}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label={t('new.contact.phone')} htmlFor="q-fix-phone" hint={t('new.contact.phoneHint')}>
          <Input id="q-fix-phone" type="tel" dir="ltr" inputMode="tel" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field
          label={t('new.contact.email')}
          htmlFor="q-fix-email"
          hint={t('new.contact.emailHint')}
          error={emailInvalid ? t('common:errors.invalidEmail') : undefined}
        >
          <Input id="q-fix-email" type="email" dir="ltr" maxLength={320} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      </div>
      <Button size="sm" variant="secondary" disabled={empty || emailInvalid} loading={save.isPending} onClick={() => save.mutate()}>
        {t('new.contact.save')}
      </Button>
    </div>
  );
}
